// Container-width hook. Returns the actual rendered width (px) of a DOM
// element. Updates whenever the element resizes — including when the
// host page's layout changes, when the viewport rotates, or when a
// sidebar opens/closes around it.
//
// Why this and not useMediaQuery: a widget embedded into a wide host
// page may still live inside a narrow column (e.g., a 60% main content
// area on a desktop site). Viewport-based queries would tell us
// "desktop" while the actual usable width is mobile-shaped. Container
// width is the truth.
//
// API note: takes an HTMLElement directly (not a ref). Callers store the
// element in state via a callback ref so the hook re-keys when the
// element mounts. The original ref-based signature missed the mount of
// elements that don't exist at Widget initialization (modal contents,
// late-rendered trees), leaving containerWidth at 0 forever.

import { useEffect, useState } from 'react';

export function useContainerWidth(el: HTMLElement | null): number {
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    if (!el) {
      setWidth(0);
      return;
    }

    // Immediate measurement on attach — ResizeObserver may not fire
    // synchronously, but the element already has its laid-out width.
    setWidth(el.offsetWidth);

    const obs = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // contentBoxSize is the modern API, but fall back to contentRect for
      // older browsers (Safari < 15.4 etc.)
      const w =
        entry.borderBoxSize?.[0]?.inlineSize ??
        entry.contentRect.width;
      setWidth(Math.round(w));
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [el]);

  return width;
}
